/** Copyright (c) 2019 T. Rowe Price. All rights reserved. */

import './EmailPhone.scss';

import classNames from 'classnames';
import { bool, func, object, string } from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';

import {
  InvalidEmails,
  NoEmails
} from 'components/layouts/ContactMethodMaintenance/EmailAddresses';
import PhoneNumberHeader from 'components/layouts/Preferences/UpdateContactInformation/EmailPhone/PhoneNumberHeader';
import { ReactComponent as WarningCircleIcon } from 'assets/icons/warning-circle.svg';
import PhoneNumberRow from 'components/layouts/Preferences/UpdateContactInformation/EmailPhone/PhoneNumberRow';
import PreferencesDataRow from 'components/layouts/Preferences/shared/PreferencesDataRow';
import PreferencesDataRowValue from 'components/layouts/Preferences/shared/PreferencesDataRowValue';
import PreferencesCol from 'components/layouts/Preferences/shared/PreferencesCol';
import PreferencesRow from 'components/layouts/Preferences/shared/PreferencesRow';
import PreferencesRowLabel from 'components/layouts/Preferences/shared/PreferencesRowLabel';
import BaseModalButton from 'components/shared/Modal/BaseModalButton';
import {
  contactInfoAddressValidation,
  handleMultipleValidations,
  isFieldRequired,
  isValidEmail
} from 'services/Validation';
import {
  getFilteredContactMethodMaintenance,
  getHasNoEmailAddress,
  getHasInvalidEmailAddress
} from 'state/contactMethodMaintenance/ContactMethodMaintenanceSelectors';
import Store from 'state/Store';
import { touchedField } from 'state/validation/ValidationActions';
import ProfileLabeledInput from 'components/layouts/Preferences/ProfileLabeledInput';
import WithUserPermissions from 'components/shared/WithUserPermissions/WithUserPermissions';
import TextInput from 'components/shared/Text/TextInput';

const emailFieldName = 'email';

const EmailWithPermissions = WithUserPermissions(TextInput);

const EmailPhone = ({
  addressType,
  contactMethodMaintenanceFiltered,
  email,
  hasInvalidEmailAddress,
  hasNoEmailAddress,
  onChange,
  phoneNumbers,
  invalidFields,
  touchedFields
}) => {
  const emailValidator = {
    field: emailFieldName,
    shouldValidate: touchedFields.email === true,
    validator: (value) =>
      handleMultipleValidations(value, [isFieldRequired, isValidEmail]),
    value: email.email.trim()
  };

  const onEmailFieldBlur = () => Store.dispatch(touchedField(emailFieldName));

  const onEmailFieldChange = (event) => {
    const value = event.target.value;
    onChange(emailFieldName, emailFieldName)(value);
  };

  const onPhoneNumberChange = (field, value) =>
    onChange('phoneNumbers', field)(value);

  const emailErrorReason = invalidFields[emailFieldName] || undefined;

  // editable-preferences-section is referenced in MailingAddress.scss
  return (
    <PreferencesRow className="email-phone-section">
      <PreferencesCol className="email-phone-title" col={3} mobile={12}>
        <PreferencesRowLabel label="Email &amp; Phone" />
        <BaseModalButton
          buttonClassName="question-icon"
          label={<WarningCircleIcon />}
          title="Email & Phone">
          <div className="email-warning-modal">
            <span className="email-warning-text-modal">
              If you change an email address, all e-Delivery services (e.g.,
              Paperless Statements) associated with that address will be
              transferred to that new e-mail address.
            </span>
          </div>
        </BaseModalButton>
      </PreferencesCol>
      <PreferencesCol
        className="editable-preferences-section"
        col={6}
        mobile={12}>
        {(hasNoEmailAddress || hasInvalidEmailAddress) && (
          <PreferencesDataRow>
            <PreferencesDataRowValue className="email-warning-row">
              {hasNoEmailAddress && <NoEmails />}
              {hasInvalidEmailAddress && (
                <InvalidEmails
                  emails={contactMethodMaintenanceFiltered.emailAddresses}
                />
              )}
            </PreferencesDataRowValue>
          </PreferencesDataRow>
        )}
        <ProfileLabeledInput
          className={classNames({ 'field-in-error': emailErrorReason })}
          errorMessage={emailErrorReason}
          label="Email"
          validators={[emailValidator]}>
          <EmailWithPermissions
            onBlur={onEmailFieldBlur}
            onChange={onEmailFieldChange}
            value={email.email}
            className="email-input"
            shouldMask
          />
        </ProfileLabeledInput>
        <PhoneNumberHeader addressType={addressType} />
        <PhoneNumberRow
          addressType={addressType}
          field="daytimePhone"
          hasExt={true}
          label="Daytime"
          onChange={onPhoneNumberChange}
          phoneNumbers={phoneNumbers}
          validations={contactInfoAddressValidation}
        />
        <PhoneNumberRow
          addressType={addressType}
          field="eveningPhone"
          hasExt={true}
          label="Evening"
          onChange={onPhoneNumberChange}
          phoneNumbers={phoneNumbers}
          validations={contactInfoAddressValidation}
        />
        <PhoneNumberRow
          addressType={addressType}
          field="mobilePhone"
          label="Mobile"
          onChange={onPhoneNumberChange}
          phoneNumbers={phoneNumbers}
          validations={contactInfoAddressValidation}
        />
        <PhoneNumberRow
          addressType={addressType}
          field="faxNumber"
          label="Fax"
          onChange={onPhoneNumberChange}
          phoneNumbers={phoneNumbers}
          validations={contactInfoAddressValidation}
        />
      </PreferencesCol>
      <PreferencesCol col={3}>
        <div className="email-warning">
          <WarningCircleIcon className="email-warning-icon" />
          <span className="email-warning-text">
            If you change an email address, all e-Delivery services (e.g.,
            Paperless Statements) associated with that address will be
            transferred to that new e-mail address.
          </span>
        </div>
      </PreferencesCol>
    </PreferencesRow>
  );
};

EmailPhone.propTypes = {
  addressType: string.isRequired,
  email: object.isRequired,
  contactMethodMaintenanceFiltered: object.isRequired,
  hasInvalidEmailAddress: bool.isRequired,
  hasNoEmailAddress: bool.isRequired,
  onChange: func.isRequired,
  phoneNumbers: object.isRequired,
  invalidFields: object.isRequired,
  touchedFields: object.isRequired
};

const mapStateToProps = (state) => ({
  contactMethodMaintenanceFiltered: getFilteredContactMethodMaintenance(state),
  hasInvalidEmailAddress: getHasInvalidEmailAddress(state),
  hasNoEmailAddress: getHasNoEmailAddress(state),
  invalidFields: state.invalidFields,
  touchedFields: state.touchedFields
});

export default connect(mapStateToProps)(EmailPhone);
